import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const ROLE_DASHBOARD_MAP = {
  ADMIN: '/system-admin',
  LANDLORD: '/landlord',
  BNB_HOST: '/airbnb-host',
  AGENT: '/agent',
  TENANT: '/listings',
  GUEST: '/listings',
};

/**
 * Determine the best dashboard route for a user based on their roles.
 * @param {string[]} roles - Uppercase role strings
 * @returns {string} Dashboard path
 */
const getDefaultDashboardForRoles = (roles) => {
  if (!roles || roles.length === 0) return '/';
  for (const role of roles) {
    if (ROLE_DASHBOARD_MAP[role]) return ROLE_DASHBOARD_MAP[role];
  }
  return '/';
};

/**
 * Guards a route by checking authentication and (optionally) role membership.
 *
 * @param {React.ReactNode} children - The element to render when access is allowed
 * @param {string[]} [allowedRoles] - Optional array of uppercase role strings required to access the route.
 *        When omitted, any authenticated user may access the route.
 *        When provided, users whose roles don't intersect are redirected to their own dashboard.
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, roles, loading } = useAuth();
  const location = useLocation();

  // While auth state is being restored on mount, render a minimal loading state.
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontFamily: 'Inter, Poppins, sans-serif',
          color: '#666',
        }}
      >
        Loading…
      </div>
    );
  }

  // Not authenticated — redirect to login, preserving the intended destination.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Role-based guard: if allowedRoles is provided and the user has none of them,
  // redirect to their own dashboard (or the home page as a fallback).
  if (allowedRoles && allowedRoles.length > 0) {
    const normalizedAllowed = allowedRoles.map((r) => String(r).toUpperCase());
    const hasAccess = roles.some((r) => normalizedAllowed.includes(String(r).toUpperCase()));
    if (!hasAccess) {
      const fallback = getDefaultDashboardForRoles(roles);
      return <Navigate to={fallback} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
